/**
 * @file PlanItemWrapper class is a wrapper of PlanItem, an entity from the backend api.
 * @author Tom Verschoore de la Houssaije.
 */
import * as moment from 'moment';
import { ColorTools } from '../../base/helpers/color-tools';
import { durationToText } from '../../core/helpers/durationToText';
/**
 * PlanItemWrapper class is a general wrapper for the PlanItem entity from the backend api.
 * Use the PlanItemWrapper class to derive specific wrappers.
 */
var PlanItemWrapper = /** @class */ (function () {
    /**
     * Constructs a PlanItemWrapper
     * @param item The PlanItem being wrapped.
     */
    function PlanItemWrapper(item) {
        this.item = item;
        /** The name of the name property. */
        this.nameProperty = 'Name';
        this.colorProp = 'Color';
        /**
         * Field to hold the entity represented by the PlanItem.
         * Use this field instead of this.item.Item.
         * @protected
         * @type {Entity}
         */
        this.entity = null;
        /** Set of color tools */
        this.colorTools = new ColorTools();
        /** Means if element starts (in time) before start of timeslot (= visual for user).  */
        this.startsEarlier = false;
        /** Means if element ends (in time) after start of timeslot (= visual for user). */
        this.endsLater = false;
        /** Gets, sets the start hour within a timeslot. */
        this.startHour = 0;
        /** Gets, sets the start minute within a timeslot. */
        this.startMinute = 0;
        /** Gets, sets the end hour within a timeslot. */
        this.endHour = 0;
        /** Gets, sets the end minute within a timeslot. */
        this.endMinute = 0;
        this.timeSpan = 0;
        this.zIndex = 0;
        this.covers = 0;
        this.weight = 0;
        this.initialize();
    }
    Object.defineProperty(PlanItemWrapper.prototype, "source", {
        /**
         * Gets the source of the plan item.
         * The source is a mapping of the item purpose.
         * @readonly
         * @type {ItemSource}
         * @memberOf PlanItemWrapper
         */
        get: function () {
            if (this.item == null || this.item.Item == null || this.item.Item['Purpose'] == null) {
                return 'other';
            }
            switch (this.item.Item['Purpose']) {
                case 'GENERATED':
                case 'FORECAST':
                    return 'forecast';
                case 'MINIMUM':
                    return 'template';
                case 'OTHER':
                    return 'other';
                default:
                    return 'other';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "duration", {
        /** Gets the duration from the PlanItem. */
        get: function () {
            var _duration = (this.isAbcense) ? moment.duration(this.MyDurationAtRealTime()) : moment.duration(this.item.Effort);
            return durationToText(_duration);
            // return moment.duration(this.item.Duration).humanize();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "color", {
        /** Gets the color form the PlanItem. */
        get: function () {
            // Return default color if entity does not have a color property
            if (!this.entity[this.colorProp] || this.entity[this.colorProp].length === 0) {
                return this.colorTools.defaultColor;
            }
            // Grap the color of the entity (replace white by default color)
            var colorValue = this.colorTools.getDisplayColor(this.entity[this.colorProp]);
            // If Entity is closed we want to see a gray scale of the color
            if (this.entity['Closed'] !== undefined && this.entity['Closed'] === true) {
                // Fix: Gesloten aanwezigheid verdwijnt soms schijnbaar uit agenda en planbord #2032
                // Was eerst 50% lichter daarmee werden sommige kleuren bijna wit en vielen weg in de achtergrond kleur.
                // Nu naar 20% dan zijn ze op mij PC nog net zichtbaar als heel licht grijs.
                // Oorspronkelijke kleur object rgb(250, 208, 159) wordt dan rgb(245, 245, 245)
                colorValue = this.colorTools.grayscale(this.colorTools.lighten(colorValue, 20));
            }
            return colorValue;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "colorDarker", {
        /** Gets the darker variant of the color */
        get: function () {
            return this.colorTools.darken(this.color, 20);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "contrast", {
        /** Gets the darker or lighten variant of the color */
        get: function () {
            return this.colorTools.contrast(this.color);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "isDraft", {
        /** Gets the flag indicating wether the PlanItem is a draft or not. */
        get: function () {
            return this.entity['Draft'];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "isFinished", {
        /** Gets the flag indicating wether the PlanItem is finished (closed/completed?). */
        get: function () {
            // TODO: Implement this.
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "start", {
        get: function () {
            return moment(this.item.Start);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "finish", {
        get: function () {
            return moment(this.item.Finish);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "isAbcense", {
        get: function () {
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "name", {
        /** Gets the name of the PlanItem. */
        get: function () {
            return this.entity[this.nameProperty];
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "weightClass", {
        get: function () {
            switch (this.weight) {
                case -1:
                    return 'sym-weight-low';
                case 1:
                    return 'sym-weight-high';
                default:
                    return 'sym-weight-normal';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanItemWrapper.prototype, "hasViolations", {
        get: function () {
            if (this.item == null) {
                return false;
            }
            return this.item.Weight > 0;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Initialize wrapper properties
     * @protected
     * @memberOf PlanItemWrapper
     */
    PlanItemWrapper.prototype.initialize = function () {
        this.entity = this.item.Item;
        this.startHour = moment(this.item.Start).hours();
        this.startMinute = moment(this.item.Start).minutes();
        this.endHour = moment(this.item.Finish).hours();
        this.endMinute = moment(this.item.Finish).minutes();
        if (this.item.Item.Weight) {
            this.weight = this.item.Item.Weight;
        }
        else {
            this.weight = 0;
        }
        this.timeSpan = moment(this.item.Finish).diff(moment(this.item.Start));
    };
    /**
     * Submits this instance to the appropriate collection of the appropriate timeslots.
     * @param timeslots Collection of timeslots that overlap this PlanItemWrapper.
     */
    PlanItemWrapper.prototype.submitToTimeslots = function (timeslots, modificationFunction, isAgenda) {
        var _this = this;
        timeslots.forEach(function (timeslot) {
            var collection = _this.getCollectionOf(timeslot);
            if (collection.indexOf(_this) === -1) {
                collection.push(modificationFunction(_this.forTimeslot(timeslot), isAgenda));
            }
        });
    };
    /**
     * Creates a appropriate copy of this instance to fit the given timeslot.
     * @param timeSlot The timeslot to fit.
     */
    PlanItemWrapper.prototype.forTimeslot = function (timeslot) {
        var result = this.makeCopy();
        result.startsEarlier = result.start.isBefore(timeslot.startDate);
        result.endsLater = result.finish.isAfter(timeslot.endDate); // timeslot.startDate.isBefore(result.finish, 'days');
        // console.log('result start: ' + result.start.format('DD:MM HH:mm') + '  timeslot start:' + timeslot.startDate.format('DD:MM HH:mm'));
        // console.log('result finish: ' + result.finish.format('DD:MM HH:mm') + '  timeslot finish:' + timeslot.endDate.format('DD:MM HH:mm'));
        // console.log('is later via isSame: ' + timeslot.endDate.isSame(result.finish, 'minutes'));
        // console.log('is later via isAfter: ' + result.finish.isAfter(timeslot.endDate, 'minutes'));
        // console.log('is next day: ' + timeslot.startDate.isBefore(result.finish, 'days'));
        // if (result.endsLater) {
        //     console.log('ends later: ' + result.finish.format('DD:MM HH:mm') + '  ' + timeslot.endDate.format('DD:MM HH:mm'));
        // } else {
        //     console.log('ends today: ' + result.finish.format('DD:MM HH:mm') + '  ' + timeslot.endDate.format('DD:MM HH:mm'));
        // }
        if (result.startsEarlier) {
            result.startHour = timeslot.startDate.hours();
            result.startMinute = timeslot.startDate.minutes();
        }
        if (result.endsLater) {
            result.endHour = timeslot.endDate.hours();
            result.endMinute = timeslot.endDate.minutes();
            // Fix for #1889
            if (result.endMinute === 59) {
                result.endMinute = 60;
            }
        }
        if ((result.endsLater || result.finish.isSame(timeslot.endDate) || timeslot.startDate.isBefore(result.finish, 'days')) && result.endHour === 0) {
            result.endHour = 24;
        }
        // Example: 01:04 ??:?? (result start) and 02:04 00:00 (result finish) and 02:04 00:00 (timeslot finish) is today (because off 00:00 of the next day), see #2365
        if (timeslot.endDate.hours() === 0 && timeslot.endDate.minutes() === 0 && result.finish.hours() === 0 && result.finish.minutes() === 0 && timeslot.startDate.diff(result.finish, 'days') === -1) {
            result.endsLater = false;
        }
        return result;
    };
    /**
     * Gets the appropriate collection of the given timeslot for this specific PlanItemWrapper class.
     * Override this method when deriving a specific class.
     */
    PlanItemWrapper.prototype.getCollectionOf = function (timeSlot) {
        throw new Error('Method getCollectionOf is not implemented. Use sub class of PlanItemWrapper instead.');
    };
    /**
     * Create a copy of this instance.
     * Override this method when deriving a specific class.
     */
    PlanItemWrapper.prototype.makeCopy = function () {
        throw new Error('Method makeCopy is not implemented. Use sub class of PlanItemWrapper instead.');
    };
    /**
     * Calculates the duration of the plan item according to its start and finish.
     * @private
     * @returns The duration as ISO string.
     */
    PlanItemWrapper.prototype.MyDurationAtRealTime = function () {
        var result = moment.duration(this.finish.diff(this.start));
        return result.toISOString();
    };
    Object.defineProperty(PlanItemWrapper.prototype, "effortAsText", {
        /**
         * Gets the effort
         */
        get: function () {
            if (!this.item || !this.item.Item || !this.item.Item['Effort']) {
                return undefined;
            }
            return durationToText(moment.duration(this.item.Item['Effort']));
        },
        enumerable: true,
        configurable: true
    });
    return PlanItemWrapper;
}());
export { PlanItemWrapper };
